import CorrectedIcon from '@/components/icon/CorrectedIcon';
import ContributionService from '@/services/Contribution.service';
import formatter from '@/utils/Formatters';
import { PriceCheckOutlined } from '@mui/icons-material';
import {
  Card,
  CardContent,
  Link,
  Skeleton,
  Tooltip,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useQueries, useQuery } from '@tanstack/react-query';

import Decimal from 'decimal.js';
import { useMemo } from 'react';
import type { FC } from 'react';

interface TotalContributionCardProps {
  sponsorId: number;
  ucid: string;
  planId: number | string;
}

const TotalContributionCard: FC<TotalContributionCardProps> = (
  props: TotalContributionCardProps
) => {
  const contribution = useQuery(
    [
      ContributionService.getContributionDetails.name,
      props.sponsorId,
      props.planId,
      props.ucid
    ],
    async () => {
      return ContributionService.getContributionDetails({
        includeCorrections: true,
        planId: props.planId,
        sponsorId: props.sponsorId,
        ucid: props.ucid
      });
    },
    {
      enabled: !!props.ucid && !!props.planId && !!props.sponsorId,
      refetchOnMount: 'always'
    }
  );

  const corrections = useQueries({
    queries:
      contribution.data?.corrections?.ucids?.map(ucid => ({
        enabled: !!contribution.data?.corrections?.ucids?.length,
        queryFn: () =>
          ContributionService.getContributionDetails({
            pageNumber: 0,
            pageSize: 0,
            planId: props.planId,
            sponsorId: props.sponsorId,
            ucid
          }),
        queryKey: [
          ContributionService.getContributionDetails.name,
          props.sponsorId,
          props.planId,
          ucid
        ],
        staleTime: Infinity
      })) ?? []
  });

  const isLoading = useMemo(
    () =>
      contribution.isLoading ||
      contribution.isFetching ||
      corrections.some(
        correction => correction.isLoading || correction.isFetching
      ),
    [corrections, contribution.isFetching, contribution.isLoading]
  );

  const total = useMemo(
    () =>
      Decimal.sum(
        contribution.data?.totals?.total ?? 0,
        contribution.data?.corrections?.totals?.total ?? 0,
        contribution.data?.matchRemediations?.totals?.total ?? 0
      ).toNumber(),
    [contribution.data]
  );

  const forfeiture = useMemo(
    () =>
      Decimal.sum(
        contribution.data?.forfeitureAmount ?? 0,
        ...corrections.map(correction => correction.data?.forfeitureAmount ?? 0)
      ).toNumber(),
    [contribution.data, corrections]
  );

  const suspense = useMemo(
    () =>
      Decimal.sum(
        contribution.data?.suspenseAmount ?? 0,
        ...corrections.map(correction => correction.data?.suspenseAmount ?? 0)
      ).toNumber(),
    [contribution.data, corrections]
  );

  const ach = useMemo(
    () =>
      Decimal.sum(
        contribution.data?.totals?.total ?? 0,
        ...corrections.map(correction =>
          new Decimal(correction.data?.totals?.total ?? 0).isNegative()
            ? 0
            : correction.data?.totals?.total ?? 0
        )
      )
        .sub(forfeiture ?? 0)
        .sub(suspense ?? 0)
        .toNumber(),
    [forfeiture, suspense, contribution.data, corrections]
  );

  return (
    <Card elevation={0} sx={{ paddingBottom: 3 }} variant='outlined'>
      <CardContent>
        <Typography data-testid='total-contribution-heading' variant='h6'>
          Total Contribution
        </Typography>
        <Grid alignItems='center' display='flex' gap={1}>
          <Typography data-testid='total-contribution' variant='h3'>
            {isLoading ? (
              <Skeleton width={200} />
            ) : (
              formatter.formatDollars(total ?? 0)
            )}
          </Typography>
          {!!contribution.data?.lostGainsUcids?.length && (
            <Tooltip data-testid='lostGainTooltip' title='Lost Gain'>
              <PriceCheckOutlined color='primary' />
            </Tooltip>
          )}
          {!!contribution.data?.corrections?.ucids?.length && (
            <Tooltip
              data-testid='correctionTooltip'
              title='This contribution has corrections'>
              <CorrectedIcon />
            </Tooltip>
          )}
          {!!contribution.data?.matchRemediations?.ucids?.length && (
            <Tooltip
              data-testid='matchRemediationsTooltip'
              title='This contribution has match remediations'>
              <CorrectedIcon />
            </Tooltip>
          )}
        </Grid>
        <Grid my={2} xs={4}>
          <Grid
            data-testid='ach-transfer-heading'
            display='flex'
            justifyContent='space-between'>
            <Typography color='gray' variant='subtitle1'>
              ACH Transfer
            </Typography>
            <Typography variant='subtitle1'>
              {isLoading ? (
                <Skeleton width={50} />
              ) : (
                formatter.formatDollars(
                  contribution.data?.doNotAchEmployer ? 0 : ach,
                  2
                )
              )}
            </Typography>
          </Grid>
          {contribution.data?.doNotAchEmployer && (
            <Grid
              data-testid='planCash-heading'
              display='flex'
              justifyContent='space-between'>
              <Typography color='gray' variant='subtitle1'>
                Plan Cash
              </Typography>
              <Typography variant='subtitle1'>
                {isLoading ? (
                  <Skeleton width={50} />
                ) : (
                  formatter.formatDollars(ach, 2)
                )}
              </Typography>
            </Grid>
          )}
          <Grid
            data-testid='forfeiture-heading'
            display='flex'
            justifyContent='space-between'>
            <Typography color='gray' variant='subtitle1'>
              Forfeiture
            </Typography>
            <Typography variant='subtitle1'>
              {isLoading ? (
                <Skeleton width={50} />
              ) : (
                formatter.formatDollars(forfeiture ?? 0, 2)
              )}
            </Typography>
          </Grid>
          <Grid
            data-testid='suspense-heading'
            display='flex'
            justifyContent='space-between'>
            <Typography color='gray' variant='subtitle1'>
              Suspense
            </Typography>
            <Typography variant='subtitle1'>
              {isLoading ? (
                <Skeleton width={50} />
              ) : (
                formatter.formatDollars(suspense ?? 0, 2)
              )}
            </Typography>
          </Grid>
          <Grid
            data-testid='rejected-heading'
            display='flex'
            justifyContent='space-between'>
            <Typography color='gray' variant='subtitle1'>
              Rejected Amount
            </Typography>
            <Typography variant='subtitle1'>
              {isLoading ? (
                <Skeleton width={50} />
              ) : (
                formatter.formatDollars(
                  contribution.data?.totals?.totalReduction,
                  2
                )
              )}
            </Typography>
          </Grid>
        </Grid>
        {!!contribution.data?.corrections?.ucids?.length && (
          <Link data-testid='correctionLink' href='#corrections'>
            View correction details
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

export default TotalContributionCard;
